import React, { useState, useEffect } from 'react';
import {
    Modal,
    Pressable,
    ScrollView,
    StyleSheet,
    Text,
    View,
} from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';

interface WaiverAcknowledgeModalProps {
    visible: boolean;
    onClose: () => void;
    onAcknowledge: () => void;
    waiverTitle?: string;
    waiverUrl?: string;
    sessionTitle?: string;
    campName?: string;
}

const WaiverAcknowledgeModal: React.FC<WaiverAcknowledgeModalProps> = ({
    visible,
    onClose,
    onAcknowledge,
    waiverTitle,
    waiverUrl,
    sessionTitle,
    campName,
}) => {
    const [agreed, setAgreed] = useState(false);

    useEffect(() => {
        if (visible) setAgreed(false);
    }, [visible]);

    const handleViewDocument = () => {
        if (waiverUrl) window.open(waiverUrl, '_blank');
    };

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
            <View style={styles.overlay}>
                <View style={styles.card}>
                    {/* Header */}
                    <View style={styles.header}>
                        <View style={styles.headerIconWrap}>
                            <MaterialCommunityIcons name="file-sign" size={22} color="#1D4ED8" />
                        </View>
                        <View style={styles.headerText}>
                            <Text style={styles.headerTitle}>Waiver Required</Text>
                            {(sessionTitle || campName) && (
                                <Text style={styles.headerSub} numberOfLines={1}>
                                    {[campName, sessionTitle].filter(Boolean).join(' · ')}
                                </Text>
                            )}
                        </View>
                        <Pressable onPress={onClose} style={styles.closeBtn}>
                            <MaterialCommunityIcons name="close" size={20} color="#94A3B8" />
                        </Pressable>
                    </View>

                    <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
                        {/* Explanation */}
                        <Text style={styles.explainerText}>
                            This camp session requires a signed waiver before registration. Please review the document below before continuing.
                        </Text>

                        {/* Document card */}
                        <Pressable
                            style={({ pressed }) => [styles.docCard, pressed && styles.docCardPressed]}
                            onPress={handleViewDocument}
                            disabled={!waiverUrl}
                        >
                            <View style={styles.docIconWrap}>
                                <MaterialCommunityIcons name="file-pdf-box" size={30} color="#DC2626" />
                            </View>
                            <View style={styles.docInfo}>
                                <Text style={styles.docName} numberOfLines={2}>
                                    {waiverTitle || 'Waiver Document'}
                                </Text>
                                <Text style={styles.docAction}>
                                    {waiverUrl ? 'Tap to view document' : 'No document attached'}
                                </Text>
                            </View>
                            {waiverUrl && (
                                <MaterialCommunityIcons name="open-in-new" size={18} color="#1D4ED8" />
                            )}
                        </Pressable>

                        {/* Steps */}
                        <View style={styles.stepsCard}>
                            <View style={styles.stepRow}>
                                <View style={styles.stepNum}>
                                    <Text style={styles.stepNumText}>1</Text>
                                </View>
                                <Text style={styles.stepText}>View and read the waiver document above.</Text>
                            </View>
                            <View style={styles.stepRow}>
                                <View style={styles.stepNum}>
                                    <Text style={styles.stepNumText}>2</Text>
                                </View>
                                <Text style={styles.stepText}>Check the acknowledgment box below if you agree.</Text>
                            </View>
                            <View style={styles.stepRow}>
                                <View style={styles.stepNum}>
                                    <Text style={styles.stepNumText}>3</Text>
                                </View>
                                <Text style={styles.stepText}>Continue to select which children to register.</Text>
                            </View>
                        </View>

                        {/* Acknowledgment checkbox */}
                        <Pressable
                            style={[styles.checkRow, agreed && styles.checkRowChecked]}
                            onPress={() => setAgreed(v => !v)}
                        >
                            <View style={[styles.checkbox, agreed && styles.checkboxChecked]}>
                                {agreed && (
                                    <MaterialCommunityIcons name="check" size={14} color="#FFFFFF" />
                                )}
                            </View>
                            <Text style={[styles.checkLabel, agreed && styles.checkLabelChecked]}>
                                I have read and agree to the waiver on behalf of my child or children registering for this session.
                            </Text>
                        </Pressable>
                    </ScrollView>

                    {/* Footer */}
                    <View style={styles.footer}>
                        <Pressable style={styles.cancelBtn} onPress={onClose}>
                            <Text style={styles.cancelBtnText}>Cancel</Text>
                        </Pressable>
                        <Pressable
                            style={[styles.continueBtn, !agreed && styles.continueBtnDisabled]}
                            onPress={agreed ? onAcknowledge : undefined}
                            disabled={!agreed}
                        >
                            <Text style={[styles.continueBtnText, !agreed && styles.continueBtnTextDisabled]}>
                                Continue to Registration
                            </Text>
                            <MaterialCommunityIcons
                                name="arrow-right"
                                size={16}
                                color={agreed ? '#FFFFFF' : '#94A3B8'}
                            />
                        </Pressable>
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.55)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    card: {
        backgroundColor: '#FFFFFF',
        borderRadius: 20,
        width: '100%',
        maxWidth: 520,
        maxHeight: '85%' as any,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.2,
        shadowRadius: 30,
        elevation: 12,
        overflow: 'hidden',
    },

    // Header
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingHorizontal: 20,
        paddingVertical: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#F1F5F9',
    },
    headerIconWrap: {
        width: 40,
        height: 40,
        borderRadius: 12,
        backgroundColor: '#EFF6FF',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    headerText: {
        flex: 1,
    },
    headerTitle: {
        fontSize: 16,
        fontWeight: '700',
        color: '#0F172A',
    },
    headerSub: {
        fontSize: 12,
        color: '#64748B',
        marginTop: 1,
    },
    closeBtn: {
        padding: 4,
    },

    // Body
    body: {
        flex: 1,
    },
    bodyContent: {
        padding: 20,
        gap: 16,
    },
    explainerText: {
        fontSize: 14,
        color: '#475569',
        lineHeight: 22,
    },

    // Document card
    docCard: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        backgroundColor: '#F8FAFC',
        borderWidth: 1.5,
        borderColor: '#E2E8F0',
        borderRadius: 14,
        padding: 14,
    },
    docCardPressed: {
        backgroundColor: '#EFF6FF',
        borderColor: '#BFDBFE',
    },
    docIconWrap: {
        width: 48,
        height: 48,
        borderRadius: 12,
        backgroundColor: '#FEF2F2',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    docInfo: {
        flex: 1,
    },
    docName: {
        fontSize: 14,
        fontWeight: '600',
        color: '#1E293B',
        lineHeight: 20,
    },
    docAction: {
        fontSize: 12,
        color: '#1D4ED8',
        marginTop: 2,
    },

    // Steps
    stepsCard: {
        backgroundColor: '#F8FAFC',
        borderRadius: 12,
        padding: 14,
        gap: 10,
    },
    stepRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 10,
    },
    stepNum: {
        width: 20,
        height: 20,
        borderRadius: 10,
        backgroundColor: '#DBEAFE',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        marginTop: 1,
    },
    stepNumText: {
        fontSize: 11,
        fontWeight: '700',
        color: '#1D4ED8',
    },
    stepText: {
        fontSize: 13,
        color: '#475569',
        lineHeight: 20,
        flex: 1,
    },

    // Checkbox
    checkRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 12,
        backgroundColor: '#F8FAFC',
        borderWidth: 1.5,
        borderColor: '#E2E8F0',
        borderRadius: 12,
        padding: 14,
    },
    checkRowChecked: {
        backgroundColor: '#EFF6FF',
        borderColor: '#93C5FD',
    },
    checkbox: {
        width: 22,
        height: 22,
        borderRadius: 6,
        borderWidth: 2,
        borderColor: '#CBD5E1',
        backgroundColor: '#FFFFFF',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        marginTop: 1,
    },
    checkboxChecked: {
        backgroundColor: '#1D4ED8',
        borderColor: '#1D4ED8',
    },
    checkLabel: {
        fontSize: 13,
        color: '#475569',
        lineHeight: 20,
        flex: 1,
    },
    checkLabelChecked: {
        color: '#1E40AF',
        fontWeight: '500',
    },

    // Footer
    footer: {
        flexDirection: 'row',
        gap: 10,
        padding: 16,
        borderTopWidth: 1,
        borderTopColor: '#F1F5F9',
    },
    cancelBtn: {
        paddingVertical: 12,
        paddingHorizontal: 20,
        borderRadius: 12,
        backgroundColor: '#F8FAFC',
        borderWidth: 1,
        borderColor: '#E2E8F0',
    },
    cancelBtnText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#64748B',
    },
    continueBtn: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        paddingVertical: 12,
        borderRadius: 12,
        backgroundColor: '#1D4ED8',
    },
    continueBtnDisabled: {
        backgroundColor: '#F1F5F9',
        borderWidth: 1,
        borderColor: '#E2E8F0',
    },
    continueBtnText: {
        fontSize: 14,
        fontWeight: '700',
        color: '#FFFFFF',
    },
    continueBtnTextDisabled: {
        color: '#94A3B8',
    },
});

export default WaiverAcknowledgeModal;
